import styled, { keyframes } from 'styled-components';
import colors from '../../assets/styles/variables/colors';

const fadeUp = keyframes`
  from { opacity: 0; transform: translateY(20px); }
  to { opacity: 1; transform: translateY(0); }
`;

export const Page = styled.div`
  padding-top: 80px;
`;

export const Hero = styled.section`
  padding: 100px 40px 80px;
  text-align: center;
  max-width: 900px;
  margin: 0 auto;
  animation: ${fadeUp} 0.8s ease-out;
`;

export const HeroBadge = styled.div`
  display: inline-block;
  padding: 8px 20px;
  border-radius: 100px;
  background: ${colors.accentGlow};
  border: 1px solid ${colors.accent}33;
  color: ${colors.accentLight};
  font-size: 13px;
  font-weight: 600;
  letter-spacing: 0.5px;
  text-transform: uppercase;
  margin-bottom: 32px;
`;

export const HeroTitle = styled.h1`
  font-size: 72px;
  font-weight: 900;
  line-height: 1.05;
  color: ${colors.text.light.very};
  letter-spacing: -3px;
  margin-bottom: 24px;

  span {
    background: linear-gradient(135deg, ${colors.accent}, ${colors.gold});
    -webkit-background-clip: text;
    -webkit-text-fill-color: transparent;
    background-clip: text;
  }

  @media (max-width: 768px) {
    font-size: 48px;
    letter-spacing: -2px;
  }
`;

export const HeroSubtitle = styled.p`
  font-size: 18px;
  line-height: 1.7;
  color: ${colors.text.light.medium};
  max-width: 680px;
  margin: 0 auto 48px;
`;

export const HeroStats = styled.div`
  display: flex;
  justify-content: center;
  gap: 48px;
  padding: 32px 0;
  border-top: 1px solid ${colors.border};

  @media (max-width: 640px) {
    gap: 24px;
    flex-wrap: wrap;
  }
`;

export const StatItem = styled.div`
  text-align: center;
`;

export const StatValue = styled.div`
  font-size: 36px;
  font-weight: 800;
  color: ${colors.text.light.very};
  letter-spacing: -1px;
`;

export const StatLabel = styled.div`
  font-size: 13px;
  color: ${colors.text.light.little};
  margin-top: 4px;
  font-weight: 500;
`;

export const Section = styled.section`
  padding: 80px 40px;
  max-width: 1200px;
  margin: 0 auto;
`;

export const SectionTitle = styled.h2`
  font-size: 40px;
  font-weight: 800;
  color: ${colors.text.light.very};
  letter-spacing: -1.5px;
  margin-bottom: 16px;
  text-align: center;
`;

export const SectionSubtitle = styled.p`
  font-size: 16px;
  color: ${colors.text.light.little};
  line-height: 1.7;
  max-width: 640px;
  margin: 0 auto 48px;
  text-align: center;
`;

export const ProjectsGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 32px;

  @media (max-width: 900px) {
    grid-template-columns: 1fr;
  }
`;

export const ProjectCard = styled.div`
  background: ${colors.bgCard};
  border: 1px solid ${colors.border};
  border-radius: 20px;
  padding: 40px;
  cursor: pointer;
  transition: all 0.3s ease;
  display: flex;
  flex-direction: column;

  &:hover {
    border-color: ${(props) => props.accentColor}55;
    box-shadow: 0 0 60px ${(props) => props.glowColor};
    transform: translateY(-4px);
  }
`;

export const CardBadge = styled.div`
  display: inline-block;
  align-self: flex-start;
  padding: 6px 14px;
  border-radius: 8px;
  background: ${(props) => props.accentColor}15;
  color: ${(props) => props.accentColor};
  font-size: 12px;
  font-weight: 600;
  letter-spacing: 0.3px;
  margin-bottom: 20px;
`;

export const CardTitle = styled.h3`
  font-size: 28px;
  font-weight: 800;
  color: ${colors.text.light.very};
  letter-spacing: -1px;
  margin-bottom: 8px;
`;

export const CardTagline = styled.p`
  font-size: 15px;
  color: ${colors.text.light.medium};
  margin-bottom: 20px;
  font-weight: 500;
`;

export const CardDescription = styled.p`
  font-size: 14px;
  color: ${colors.text.light.little};
  line-height: 1.8;
  flex: 1;
  margin-bottom: 20px;
`;

export const AiChip = styled.div`
  display: inline-flex;
  align-self: flex-start;
  align-items: center;
  gap: 6px;
  padding: 6px 14px;
  border-radius: 8px;
  background: linear-gradient(135deg, ${(props) => props.accentColor}10, ${colors.accent}10);
  border: 1px solid ${(props) => props.accentColor}25;
  color: ${(props) => props.accentColor};
  font-size: 12px;
  font-weight: 600;
  margin-bottom: 24px;
`;

export const CardFooter = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding-top: 20px;
  border-top: 1px solid ${colors.border};
  font-size: 13px;
  color: ${colors.text.light.little};
`;

export const ViewButton = styled.span`
  display: flex;
  align-items: center;
  gap: 6px;
  color: ${(props) => props.accentColor};
  font-weight: 600;
  font-size: 14px;
  transition: gap 0.2s;

  ${ProjectCard}:hover & {
    gap: 10px;
  }
`;

export const TechGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 24px;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`;

export const TechCard = styled.div`
  background: ${colors.bgCard};
  border: 1px solid ${colors.border};
  border-radius: 16px;
  padding: 32px;
`;

export const TechCardTitle = styled.h4`
  font-size: 18px;
  font-weight: 700;
  color: ${colors.text.light.very};
  margin-bottom: 16px;
`;

export const TechOptions = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  margin-bottom: 16px;
`;

export const TechOption = styled.span`
  padding: 6px 16px;
  border-radius: 8px;
  background: ${colors.accentGlow};
  border: 1px solid ${colors.accent}22;
  color: ${colors.accentLight};
  font-size: 13px;
  font-weight: 600;
  font-family: 'JetBrains Mono', monospace;
`;

export const TechNote = styled.p`
  font-size: 13px;
  color: ${colors.text.light.little};
  line-height: 1.7;
`;

export const EvalGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 24px;

  @media (max-width: 900px) {
    grid-template-columns: repeat(2, 1fr);
  }

  @media (max-width: 600px) {
    grid-template-columns: 1fr;
  }
`;

export const EvalCard = styled.div`
  background: ${colors.bgCard};
  border: 1px solid ${colors.border};
  border-radius: 16px;
  padding: 32px;
  transition: border-color 0.2s;

  &:hover {
    border-color: ${colors.borderHover};
  }
`;

export const EvalNumber = styled.div`
  font-size: 32px;
  font-weight: 800;
  color: ${colors.accent}40;
  font-family: 'JetBrains Mono', monospace;
  margin-bottom: 12px;
`;

export const EvalTitle = styled.h4`
  font-size: 17px;
  font-weight: 700;
  color: ${colors.text.light.very};
  margin-bottom: 12px;
`;

export const EvalDescription = styled.p`
  font-size: 13px;
  color: ${colors.text.light.little};
  line-height: 1.8;
  margin-bottom: 16px;
`;

export const EvalWeight = styled.div`
  display: inline-block;
  padding: 4px 12px;
  border-radius: 6px;
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.5px;
  text-transform: uppercase;
  background: ${(props) =>
    props.weight === 'Critical'
      ? colors.coral + '18'
      : props.weight === 'High'
      ? colors.gold + '18'
      : colors.green + '18'};
  color: ${(props) =>
    props.weight === 'Critical'
      ? colors.coral
      : props.weight === 'High'
      ? colors.gold
      : colors.green};
`;

export const SubmissionSection = styled.div`
  max-width: 700px;
  margin: 0 auto;
`;

export const SubFormat = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 12px;
  margin-bottom: 32px;
  padding: 24px;
  background: ${colors.bgCard};
  border: 1px solid ${colors.border};
  border-radius: 12px;
  font-size: 14px;
  color: ${colors.text.light.medium};

  strong {
    color: ${colors.text.light.very};
  }

  span {
    color: ${colors.text.light.little};
  }
`;

export const SubFormatCode = styled.code`
  padding: 6px 12px;
  background: ${colors.accentGlow};
  border: 1px solid ${colors.accent}22;
  border-radius: 6px;
  color: ${colors.accentLight};
  font-family: 'JetBrains Mono', monospace;
  font-size: 13px;
`;

export const SubmissionBox = styled.div`
  display: flex;
  flex-direction: column;
  gap: 24px;
  padding: 32px;
  background: ${colors.bgCard};
  border: 1px solid ${colors.border};
  border-radius: 16px;
  margin-bottom: 24px;
`;

export const SubReqTitle = styled.h4`
  font-size: 16px;
  font-weight: 700;
  color: ${colors.text.light.very};
  margin-bottom: 8px;
`;

export const SubReqDetail = styled.p`
  font-size: 14px;
  color: ${colors.text.light.little};
  line-height: 1.7;
`;

export const SubImportant = styled.div`
  padding: 20px 24px;
  background: ${colors.gold}08;
  border: 1px solid ${colors.gold}25;
  border-radius: 12px;
  color: ${colors.gold};
  font-size: 14px;
  font-weight: 500;
  line-height: 1.7;
`;

export const RoleSection = styled.section`
  padding: 40px;
`;

export const RoleDescription = styled.p`
  font-size: 15px;
  color: ${colors.text.light.little};
  line-height: 1.7;
  max-width: 640px;
  margin: 0 auto 40px;
  text-align: center;
`;

export const RoleGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 24px;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`;

export const RoleCard = styled.div`
  background: ${colors.bgCard};
  border: 1px solid ${colors.border};
  border-radius: 16px;
  padding: 32px;
  transition: border-color 0.2s;

  &:hover {
    border-color: ${colors.borderHover};
  }
`;

export const PackageBadge = styled.div`
  display: inline-block;
  margin: 0 auto 24px;
  padding: 10px 28px;
  border-radius: 12px;
  background: ${colors.greenGlow};
  border: 1px solid ${colors.green}30;
  color: ${colors.green};
  font-size: 20px;
  font-weight: 800;
  letter-spacing: -0.5px;
  text-align: center;
  width: fit-content;
  display: block;
  margin-left: auto;
  margin-right: auto;
`;

export const RoleTitle = styled.h4`
  font-size: 17px;
  font-weight: 700;
  color: ${colors.text.light.very};
  margin-bottom: 8px;
`;

export const RoleStack = styled.div`
  display: inline-block;
  padding: 4px 12px;
  border-radius: 6px;
  background: ${colors.accentGlow};
  border: 1px solid ${colors.accent}22;
  color: ${colors.accentLight};
  font-size: 12px;
  font-weight: 600;
  font-family: 'JetBrains Mono', monospace;
  margin-bottom: 14px;
`;

export const RoleSignals = styled.p`
  font-size: 13px;
  color: ${colors.text.light.little};
  line-height: 1.8;
`;
